import React, { useMemo } from "react";
import Select from "./ui/Select";
import { iranCustoms } from "../constants/iranCustoms";

// shared select styles (same as invoices)
const selectStyles = {
  control: (p) => ({
    ...p,
    fontFamily: "Vazirmatn, sans-serif",
    direction: "rtl",
    borderColor: "#E5E7EB",
    borderRadius: "0.75rem",
    paddingBlock: "2px",
    boxShadow: "none",
    backgroundColor: "#FFFFFF",
    minHeight: "42px",
    ":hover": { borderColor: "#2563EB" },
  }),
  placeholder: (p) => ({ ...p, color: "#9CA3AF", textAlign: "right" }),
  singleValue: (p) => ({ ...p, color: "#111827", textAlign: "right" }),
  multiValue: (p) => ({
    ...p,
    backgroundColor: "#EEF2FF",
    borderRadius: "0.5rem",
  }),
  multiValueLabel: (p) => ({ ...p, color: "#111827" }),
  multiValueRemove: (p) => ({
    ...p,
    ":hover": { backgroundColor: "#2563EB", color: "#fff" },
  }),
  menu: (p) => ({
    ...p,
    fontFamily: "Vazirmatn, sans-serif",
    direction: "rtl",
    borderRadius: "0.75rem",
    overflow: "hidden",
  }),
  option: (p, state) => ({
    ...p,
    textAlign: "right",
    backgroundColor: state.isSelected
      ? "#2563EB"
      : state.isFocused
      ? "#DBEAFE"
      : "#fff",
    color: state.isSelected ? "#fff" : "#111827",
  }),
};

const ProformaInvoiceDetails = ({
  invoice,
  onFieldChange,
  sellerOptions,
  buyerOptions,
  currencyOptions,
  termsOfDeliveryOptions,
  meansOfTransportOptions,
  termsOfPaymentOptions,
  standardOptions,
  countryOptions,
}) => {
  const iranCustomsOptions = useMemo(
    () =>
      iranCustoms.map((c) => ({
        value: c.ctmNameStr,
        label: `${c.ctmNameStr} (${c.ctmVCodeInt})`,
      })),
    []
  );

  return (
    <div dir="rtl" className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Seller */}
      <div className="space-y-1">
        <label className="text-sm text-gray-600">فروشنده</label>
        <Select
          styles={selectStyles}
          options={sellerOptions}
          value={sellerOptions.find((o) => o.value === invoice.seller) || null}
          onChange={(opt) => onFieldChange("seller", opt ? opt.value : "")}
          placeholder="انتخاب فروشنده"
          isClearable
        />
      </div>

      {/* Buyer */}
      <div className="space-y-1">
        <label className="text-sm text-gray-600">خریدار</label>
        <Select
          styles={selectStyles}
          options={buyerOptions}
          value={buyerOptions.find((o) => o.value === invoice.buyer) || null}
          onChange={(opt) => onFieldChange("buyer", opt ? opt.value : "")}
          placeholder="انتخاب خریدار"
          isClearable
        />
      </div>

      {/* Invoice Number */}
      <div className="space-y-1">
        <label className="text-sm text-gray-600">شماره فاکتور</label>
        <input
          className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
          value={invoice.proforma_invoice_number || ""}
          onChange={(e) =>
            onFieldChange("proforma_invoice_number", e.target.value)
          }
          placeholder="شماره فاکتور"
          required
        />
      </div>

      {/* Invoice Date */}
      <div className="space-y-1">
        <label className="text-sm text-gray-600">تاریخ فاکتور</label>
        <input
          type="date"
          className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
          value={invoice.proforma_invoice_date || ""}
          onChange={(e) =>
            onFieldChange("proforma_invoice_date", e.target.value)
          }
          required
        />
      </div>

      {/* Expiry Date */}
      <div className="space-y-1">
        <label className="text-sm text-gray-600">تاریخ اعتبار فاکتور</label>
        <input
          type="date"
          className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
          value={invoice.proforma_invoice_exp_date || ""}
          onChange={(e) =>
            onFieldChange("proforma_invoice_exp_date", e.target.value)
          }
          required
        />
      </div>

      {/* Currency */}
      <div className="space-y-1">
        <label className="text-sm text-gray-600">ارز</label>
        <Select
          styles={selectStyles}
          options={currencyOptions}
          value={
            currencyOptions.find(
              (o) => o.value === invoice.proforma_invoice_currency
            ) || null
          }
          onChange={(opt) =>
            onFieldChange("proforma_invoice_currency", opt ? opt.value : "")
          }
          placeholder="انتخاب ارز"
          isClearable
        />
      </div>

      {/* Freight Charges */}
      <div className="space-y-1">
        <label className="text-sm text-gray-600">هزینه حمل</label>
        <input
          type="number"
          onWheel={(e) => e.target.blur()}
          className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
          value={invoice.proforma_freight_charges || 0}
          onChange={(e) =>
            onFieldChange("proforma_freight_charges", e.target.value)
          }
          placeholder="هزینه حمل"
        />
      </div>

      {/* Country of Origin (multi) */}
      <div className="space-y-1">
        <label className="text-sm text-gray-600">کشور مبدأ</label>
        <Select
          isMulti
          styles={selectStyles}
          options={countryOptions}
          value={
            invoice.country_of_origin
              ? invoice.country_of_origin
                  .split("-")
                  .map((v) => countryOptions.find((o) => o.value === v.trim()))
              : []
          }
          onChange={(opts) =>
            onFieldChange(
              "country_of_origin",
              (opts || []).map((o) => o.value).join("-")
            )
          }
          placeholder="انتخاب کشور مبدأ"
        />
      </div>

      {/* Port of Loading (multi) */}
      <div className="space-y-1">
        <label className="text-sm text-gray-600">بندر بارگیری</label>
        <Select
          isMulti
          styles={selectStyles}
          options={countryOptions}
          value={
            invoice.port_of_loading
              ? invoice.port_of_loading
                  .split("-")
                  .map((v) => countryOptions.find((o) => o.value === v.trim()))
              : []
          }
          onChange={(opts) =>
            onFieldChange(
              "port_of_loading",
              (opts || []).map((o) => o.value).join("-")
            )
          }
          placeholder="انتخاب بندر بارگیری"
        />
      </div>

      {/* Terms of Delivery */}
      <div className="space-y-1">
        <label className="text-sm text-gray-600">شرایط تحویل</label>
        <Select
          styles={selectStyles}
          options={termsOfDeliveryOptions}
          value={
            termsOfDeliveryOptions.find(
              (o) => o.value === invoice.terms_of_delivery
            ) || null
          }
          onChange={(opt) =>
            onFieldChange("terms_of_delivery", opt ? opt.value : "")
          }
          placeholder="انتخاب شرایط تحویل"
          isClearable
        />
      </div>

      {/* Terms of Payment */}
      <div className="space-y-1">
        <label className="text-sm text-gray-600">شرایط پرداخت</label>
        <Select
          styles={selectStyles}
          options={termsOfPaymentOptions}
          value={
            termsOfPaymentOptions.find(
              (o) => o.value === invoice.terms_of_payment
            ) || null
          }
          onChange={(opt) =>
            onFieldChange("terms_of_payment", opt ? opt.value : "")
          }
          placeholder="انتخاب شرایط پرداخت"
          isClearable
        />
      </div>

      {/* Standard */}
      <div className="space-y-1">
        <label className="text-sm text-gray-600">استاندارد</label>
        <Select
          styles={selectStyles}
          options={standardOptions}
          value={
            standardOptions.find((o) => o.value === invoice.standard) || null
          }
          onChange={(opt) => onFieldChange("standard", opt ? opt.value : "")}
          placeholder="انتخاب استاندارد"
          isClearable
        />
      </div>

      {/* Means of Transport (multi) */}
      <div className="space-y-1">
        <label className="text-sm text-gray-600">وسیله حمل</label>
        <Select
          isMulti
          styles={selectStyles}
          options={meansOfTransportOptions}
          value={
            invoice.means_of_transport
              ? invoice.means_of_transport
                  .split("-")
                  .map((v) =>
                    meansOfTransportOptions.find((o) => o.value === v.trim())
                  )
              : []
          }
          onChange={(opts) =>
            onFieldChange(
              "means_of_transport",
              (opts || []).map((o) => o.value).join("-")
            )
          }
          placeholder="انتخاب وسیله حمل"
        />
      </div>

      {/* Partial shipment */}
      <div className="space-y-1 flex items-center gap-2">
        <input
          id="partial_shipment"
          type="checkbox"
          checked={Boolean(invoice.partial_shipment)}
          onChange={(e) => onFieldChange("partial_shipment", e.target.checked)}
          className="h-5 w-5 rounded border-gray-300"
        />
        <label htmlFor="partial_shipment" className="text-sm text-gray-700">
          حمل به دفعات
        </label>
      </div>

      {/* Customs Destination (multi) */}
      <div className="space-y-1 md:col-span-2">
        <label className="text-sm text-gray-600">گمرک مقصد</label>
        <Select
          isMulti
          styles={selectStyles}
          options={iranCustomsOptions}
          
          value={
            invoice.relevant_location
              ? invoice.relevant_location
                  .split("-")
                  .map((v) =>
                    iranCustomsOptions.find((o) => o.value === v.trim())
                  )
              : []
          }
          onChange={(opts) =>
            onFieldChange(
              "relevant_location",
              (opts || []).map((o) => o.value).join("-")
            )
          }
          placeholder="انتخاب گمرک مقصد"
        />
      </div>
    </div>
  );
};

export default ProformaInvoiceDetails;
